/**
 * The browser's copy of the coach first-run facts (issue #755), the coach
 * counterpart of `lib/onboarding.js`.
 *
 * THE SERVER DECIDES; THIS FILE ONLY NAMES THE ROUTE. `api/src/lib/
 * coachSetup.ts`'s `isCoachSetupDone` is the authority, assembled server-
 * side in `routes/coachProfiles.ts` and returned as `coach_setup_required`
 * on `GET`/`PUT /coach-profiles/me` (#770's "no new route" instruction —
 * see that route's own comment). `CoachSetupContext.js` reads the one
 * boolean and obeys; nothing here re-derives it.
 */
import { newUi } from "./featureFlags";

/** Where the coach set-up screen lives — one route, no steps/query params
 * (unlike `/onboarding`, this is a single screen). */
export const COACH_SETUP_PATH = "/coach-setup";

/**
 * Issue #756: the empty coach portal's invite link and gold action both
 * need to open "the invite step of the coach set-up" — but #755 shipped
 * `/coach-setup` as one screen with no steps (see `COACH_SETUP_PATH`'s own
 * comment above), so there is no second route or query param to point at.
 * The one string agreed between the two slices is this in-page anchor:
 * `CoachSetUp.jsx` puts `id={COACH_SETUP_INVITE_ANCHOR}` on its invite
 * section, and `ScrollToTop.jsx` already scrolls any `#hash` destination
 * to the element with that id if one exists — no new scroll code needed,
 * just the one shared id both files import from here.
 */
export const COACH_SETUP_INVITE_ANCHOR = "coach-setup-invite";

/**
 * The fallback when the coach set-up screen is off (#1544): Settings with More
 * opened for the visit (`?open=more`, which `SettingsScreen` honours) and the
 * hash naming the Add a player card it then scrolls into view. Without the
 * query the card sits inside the closed More fold, so the tap would not open
 * the invite form.
 */
export const SETTINGS_ADD_PLAYER_PATH = "/settings?open=more#settings-add-player";

/** `/coach-setup#coach-setup-invite` — the one destination `CoachPortal.jsx`'s
 * empty-state link and its gold "Invite a player" action both use (#756).
 * NEVER pass this straight to a `Link`/`nav()` call site — see
 * `coachSetupInviteHref` below, which is the flag-safe wrapper every call
 * site must use instead. */
export const COACH_SETUP_INVITE_PATH = `${COACH_SETUP_PATH}#${COACH_SETUP_INVITE_ANCHOR}`;

/**
 * #795 review round, F1 (the general problem is #800; this fixes only this
 * screen's own link, per #800's own instruction not to solve the general
 * case here). `newUi` is PER SCREEN (`lib/featureFlags.js`), not global:
 * `nw_new_ui = "coach-portal-empty"` with `coach-setup` left off is a
 * legal, reachable runtime state (a screen turned on alone, exactly what
 * per-screen rollout is FOR), and in that state `COACH_SETUP_INVITE_PATH`
 * is a dead link — `App.js:273-275` renders `<Navigate to="/coach-portal"
 * replace />` for `/coach-setup` whenever `newUi("coach-setup")` is false,
 * so both the empty portal's card link and its gold action would bounce
 * the coach straight back to the page they're already on.
 *
 * This resolves the ACTUAL destination at call time: the coach set-up's
 * invite anchor when that screen is itself on, else the shipped fallback
 * (`/settings`) `CoachPortal.jsx` used before #756 ever existed — never a
 * route that redirects back to the caller. #756 anticipated this exact
 * shape ("If the coach set-up is not merged yet, point at /settings as
 * today"); merged-but-flag-gated is the same hole, just reachable later.
 *
 * Every cross-screen link a redrawn screen writes should resolve through a
 * helper shaped like this one — #800 is the decision about making that a
 * rule, not just a habit; this function is this screen's own instance of
 * it, nothing more.
 */
export function coachSetupInviteHref() {
  return newUi("coach-setup") ? COACH_SETUP_INVITE_PATH : SETTINGS_ADD_PLAYER_PATH;
}
